/**
 * A casa, como o servidor a devolve. Espelha `CASA_PADRAO`/`normalizarCasa`
 * em `server/src/casa.js` — se um campo mudar lá, muda aqui.
 */

export interface Pessoa {
  slug: string;
  nome: string;
  pronomes: string;
  /** Hex com `#`. O servidor troca por `#F4C2C2` o que não casar. */
  cor: string;
  /** IANA, validado por `Intl` do lado do servidor. */
  fuso: string;
}

/**
 * Os três eixos da relação, como os enums de `lib/core/config/couple_config.dart`
 * e as listas de `server/src/casa.js`. A ordem é a de lá: é a ordem em que as
 * escolhas aparecem na tela.
 */
export const ESTRUTURAS = [
  { id: 'fechada', rotulo: 'Fechada' },
  { id: 'aberta', rotulo: 'Aberta' },
  { id: 'poli', rotulo: 'Poliamorosa' },
  { id: 'anarquia', rotulo: 'Sem hierarquia' },
] as const;

export const FORMATOS = [
  { id: 'junto', rotulo: 'Dividindo a mesma casa' },
  { id: 'perto', rotulo: 'Em casas separadas' },
  { id: 'longe', rotulo: 'Em fusos diferentes' },
] as const;

export const MARCOS = [
  { id: 'comecando', rotulo: 'Começando' },
  { id: 'namorando', rotulo: 'Namorando' },
  { id: 'noivos', rotulo: 'Noivos' },
  { id: 'casados', rotulo: 'Casados' },
  { id: 'anos', rotulo: 'Juntos há anos' },
] as const;

export type Estrutura = (typeof ESTRUTURAS)[number]['id'];
export type Formato = (typeof FORMATOS)[number]['id'];
export type Marco = (typeof MARCOS)[number]['id'];

/** O teto do servidor (`MAX_PESSOAS`): de uma a oito pessoas por casa. */
export const MAX_PESSOAS = 8;

/**
 * `Estrutura.cabeEm`: monogamia com três pessoas não existe.
 *
 * A mesma regra que o servidor aplica ao gravar, dita antes de virar erro — lá
 * uma casa de três com estrutura "fechada" volta corrigida para "poli".
 */
export function estruturaCabeEm(id: Estrutura, pessoas: number): boolean {
  return pessoas <= 2 || (id !== 'fechada' && id !== 'aberta');
}

/** "Ana Beatriz" -> "ana-beatriz". O servidor confere de novo e manda o que ficou. */
export function slugDe(nome: string): string {
  const de = 'áàâãäéèêëíìîïóòôõöúùûüçñ';
  const para = 'aaaaaeeeeiiiiooooouuuucn';
  return Array.from(nome.trim().toLowerCase())
    .map((c) => {
      const i = de.indexOf(c);
      return i >= 0 ? para[i] : c;
    })
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface Casa {
  configurada: boolean;
  /** Slug de quem administra. Só essa pessoa grava; ver `podeMexer`. */
  lider: string;
  estrutura: Estrutura;
  formato: Formato;
  marco: Marco;
  pessoas: Pessoa[];
  /**
   * Só vem no `GET /casa`, calculado por pedido a partir da sessão. Quem não
   * lidera recebe 202 (virou sugestão) em vez de 403 ao tentar gravar.
   */
  podeMexer?: boolean;
}

/**
 * O nome da casa, como no `Casa.nome` do Flutter: duas pessoas viram
 * "Fulana & Beltrano", três ou mais viram "Fulana, Beltrano e Sicrana".
 *
 * A porta pede este nome antes de existir sessão, e por isso ele não pode
 * depender do `GET /casa`. O padrão embutido é o mesmo `Casa.padrao` de lá —
 * sai daqui no dia em que a tela de configuração existir.
 */
export function nomeDaCasa(casa?: Casa | null): string {
  const nomes = (casa?.pessoas ?? []).map((p) => p.nome).filter(Boolean);
  if (nomes.length === 0) return 'Mohamed & Isabelle';
  if (nomes.length === 1) return nomes[0];
  if (nomes.length === 2) return `${nomes[0]} & ${nomes[1]}`;
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/**
 * `CoupleConfig.outros`: todo mundo da casa menos essa pessoa.
 *
 * Numa casa de três não existe *a* outra pessoa: existem as outras. Quem não
 * é da casa recebe lista vazia, e não a casa inteira — o comentário de lá é
 * que "um aviso que sai para uma e cala com a outra é pior do que aviso
 * nenhum".
 */
export function outrosDaCasa(casa: Casa | null | undefined, slug?: string | null): Pessoa[] {
  const pessoas = casa?.pessoas ?? [];
  if (!slug || !pessoas.some((p) => p.slug === slug)) return [];
  return pessoas.filter((p) => p.slug !== slug);
}

/** `CoupleConfig.nomeDosOutros`: "Bia", "Bia e Caio". Vazio para quem mora só. */
export function nomeDosOutros(casa: Casa | null | undefined, slug?: string | null): string {
  const nomes = outrosDaCasa(casa, slug).map((p) => p.nome);
  if (nomes.length === 0) return '';
  if (nomes.length === 1) return nomes[0];
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}
